import { sdkRequest } from '@eventbrite/http';
import React, { useEffect, useState } from 'react';
import type { OrganizerInfoExperimentValues } from '../../../types';
import {
    followOrganizer,
    getfollowOrganizerChannelsGetURL,
    setFollowStatus,
    unfollowOrganizer,
} from './api';
import type { FollowButtonProps } from './FollowButton';
import './FollowButton.scss';

type FollowButtonProviderProps = {
    organizerPanelVariantVersion: Exclude<
        OrganizerInfoExperimentValues,
        OrganizerInfoExperimentValues.default
    >;
    isGDPRCountry: boolean;
    organizerId: string;
    gaSettings: any;
    userId?: number;
};

const hasChosenGDPR = (response: any) => {
    const explicitOptIn = 1;
    const explicitOptOut = 2;
    // Array with the values if the user has explicitly opted in or out for receiving notifications from an organizer
    const explicitChoices = [explicitOptIn, explicitOptOut];
    const hasChosen =
        explicitChoices.includes(
            response['preferences'][0]['global_preference'],
        ) &&
        explicitChoices.includes(
            response['preferences'][1]['global_preference'],
        );

    return hasChosen;
};

const updateFollowState = (followedByYou: boolean, followersCount: number) =>
    followedByYou ? followersCount + 1 : followersCount - 1;

export const FollowButtonContext =
    React.createContext<FollowButtonProps | null>(null);

export const FollowButtonProvider: React.FC<FollowButtonProviderProps> = ({
    organizerPanelVariantVersion,
    children,
    organizerId,
    userId,
    isGDPRCountry,
}) => {
    const [isFollowing, setIsFollowing] = useState(false);
    const [followersCount, setFollowersCount] = useState<number | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);

    const [shouldShowLoginModal, setShouldShowLoginModal] = useState(false);
    const [shouldShowGDPRModal, setShouldShowGDPRModal] = useState(false);

    useEffect(() => {
        setFollowStatus({ organizerId, setIsFollowing, setFollowersCount });
    }, [organizerId]);

    const onChangeFollow = () => {
        const followUnfollowResponseHandler = (response: any) => {
            if (response) {
                const { action_performed, followed_by_you } = response;
                if (action_performed && followed_by_you !== undefined) {
                    setIsFollowing(followed_by_you);
                    setFollowersCount(
                        updateFollowState(
                            followed_by_you,
                            followersCount as number,
                        ),
                    );
                    setIsUpdating(false);

                    if (followed_by_you && userId && isGDPRCountry) {
                        // checks if user has given explicit opt in or opt out
                        // to show GDPR modal in case it hasn't
                        sdkRequest(getfollowOrganizerChannelsGetURL(userId))
                            .then((response: any) => {
                                setShouldShowGDPRModal(
                                    !hasChosenGDPR(response),
                                );
                            })
                            .catch(() => {
                                setShouldShowGDPRModal(true);
                            });
                    }
                }
            }
        };

        if (!isUpdating) {
            if (isFollowing) {
                unfollowOrganizer({
                    organizerId,
                    followUnfollowResponseHandler,
                    setIsUpdating,
                    setShouldShowLoginModal,
                });
            } else {
                followOrganizer({
                    organizerId,
                    followUnfollowResponseHandler,
                    setIsUpdating,
                    setShouldShowLoginModal,
                });
            }
        }
    };

    return (
        <FollowButtonContext.Provider
            value={{
                organizerPanelVariantVersion,
                isFollowing,
                onChangeFollow,
                followersCount,
                shouldShowLoginModal,
                closeLoginModal: () => {
                    setShouldShowLoginModal(false);
                },
                shouldShowGDPRModal,
                closeGDPRModal: () => {
                    setShouldShowGDPRModal(false);
                },
                userId,
            }}
        >
            {children}
        </FollowButtonContext.Provider>
    );
};
